/* sm-home-delivery-route.js — Homepage delivery section (templates/index.liquid § 6).
 *
 * Reads the published van runs from the Wardrobe Designer app
 * (GET /api/v1/delivery-routes — the same data as the delivery page block),
 * picks the next run customers can still order for, and:
 *   - draws its stops as a dashed line + dots on the orange Australia map
 *   - lists its towns as chips
 *   - fills the "Next run … Order by …" line
 *   - points "Check Van Delivery Dates" at that route's tab on /pages/delivery
 *
 * If nothing loads or no run is open, the section keeps its fallback copy and
 * the map shows only the Carrara dot.
 */
(function () {
  "use strict";

  /*
   * The orange map (snippets/global_aus_map.liquid) is a drawn shape, not a
   * real map. These numbers turn a real location into a spot on the shape.
   * Worked out from the capital-city dots in the snippet, then shifted so
   * Carrara lands exactly on its white dot (178.19, 83.3).
   */
  function toMap(lat, lng) {
    return {
      x: 4.2216 * lng + 0.0608 * lat - 467.5689,
      y: -0.168 * lng - 5.3436 * lat - 40.7106,
    };
  }

  /* Land shapes from the snippet, used to pull coastal towns back onto land. */
  var LAND = [
    [136.26,150.38,158.73,150.38,152.22,165.64,143.79,167.75],
    [134.47,0,125.34,33.7,116.9,30.12,117.36,72.51,127.07,72.53,127.07,85.74,181.25,85.74,182.15,83.23],
    [127.07,87.71,127.07,117.99,162.97,136.6,180.54,87.71],
    [127.06,141.72,129.81,145.15,161.63,138.13,127.06,120.28],
    [125.06,74.55,70.44,74.55,70.4,117.87,112.5,123.57,125.05,139.22],
    [114.94,29.29,105.62,25.34,109.26,9.51,79.58,7.28,70.39,23.06,70.4,72.36,115.41,72.62],
    [68.44,24.39,55.9,20.49,0,75.25,25.86,134.72,66.7,117.37,68.42,117.61],
  ];
  var MAP_CENTRE = { x: 110, y: 85 };
  var CARRARA = { x: 178.19, y: 83.3 };

  function inside(poly, x, y) {
    var hit = false;
    for (var i = 0, j = poly.length - 2; i < poly.length; j = i, i += 2) {
      var xi = poly[i], yi = poly[i + 1], xj = poly[j], yj = poly[j + 1];
      if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
        hit = !hit;
      }
    }
    return hit;
  }

  function onLand(point) {
    for (var i = 0; i < LAND.length; i += 1) {
      if (inside(LAND[i], point.x, point.y)) return true;
    }
    return false;
  }

  /* Step towards the middle of the map until the point sits on the shape. */
  function pullOntoLand(point) {
    var p = { x: point.x, y: point.y };
    for (var step = 0; step < 40 && !onLand(p); step += 1) {
      var dx = MAP_CENTRE.x - p.x, dy = MAP_CENTRE.y - p.y;
      var len = Math.sqrt(dx * dx + dy * dy) || 1;
      p.x += (dx / len) * 0.5;
      p.y += (dy / len) * 0.5;
    }
    /* Keep dots clear of the shape's edge so they don't look cut off. */
    var dx2 = MAP_CENTRE.x - p.x, dy2 = MAP_CENTRE.y - p.y;
    var len2 = Math.sqrt(dx2 * dx2 + dy2 * dy2) || 1;
    if (!onLand({ x: p.x + (dx2 / len2) * -1.6, y: p.y + (dy2 / len2) * -1.6 })) {
      p.x += (dx2 / len2) * 1.2;
      p.y += (dy2 / len2) * 1.2;
    }
    return p;
  }

  function isCarrara(point) {
    var dx = point.x - CARRARA.x, dy = point.y - CARRARA.y;
    return dx * dx + dy * dy < 4;
  }

  function todayInBrisbane() {
    try {
      return new Intl.DateTimeFormat("en-CA", {
        timeZone: "Australia/Brisbane",
      }).format(new Date());
    } catch (err) {
      return new Date().toISOString().slice(0, 10);
    }
  }

  /*
   * Next run customers can still order for: open, earliest delivery first.
   * If none is open, fall back to the most recent dated run (then the first
   * route) so the map still shows a route; `upcoming` is false so the
   * "Next run / Order by" line stays hidden.
   */
  function pickRoute(routes) {
    var today = todayInBrisbane();
    var usable = routes.filter(function (route) {
      return route && Array.isArray(route.stops) && route.stops.length >= 2;
    });
    var byStart = function (a, b) {
      return String(a.deliveryStartsOn || "").localeCompare(String(b.deliveryStartsOn || ""));
    };
    var open = usable
      .filter(function (route) {
        if (route.rateOpen !== true) return false;
        return !route.orderByDate || route.orderByDate >= today;
      })
      .sort(byStart)[0];
    if (open) return { route: open, upcoming: true };
    var dated = usable.filter(function (route) { return route.deliveryStartsOn; }).sort(byStart);
    var latest = dated[dated.length - 1] || usable[0];
    return latest ? { route: latest, upcoming: false } : null;
  }

  var SVG_NS = "http://www.w3.org/2000/svg";

  function drawRoute(layer, route) {
    var points = route.stops
      .filter(function (stop) {
        return Number.isFinite(Number(stop.latitude)) && Number.isFinite(Number(stop.longitude));
      })
      .map(function (stop) {
        var p = pullOntoLand(toMap(Number(stop.latitude), Number(stop.longitude)));
        return { x: p.x, y: p.y, label: stop.label };
      });
    if (points.length < 2) return false;

    var line = document.createElementNS(SVG_NS, "polyline");
    line.setAttribute(
      "points",
      points.map(function (p) { return p.x.toFixed(2) + "," + p.y.toFixed(2); }).join(" ")
    );
    line.setAttribute("class", "sm-aus-route__line");
    layer.appendChild(line);

    var drawn = [];
    points.forEach(function (p) {
      if (isCarrara(p)) return; /* Carrara already has its own dot */
      var dupe = drawn.some(function (q) {
        var dx = q.x - p.x, dy = q.y - p.y;
        return dx * dx + dy * dy < 1.5;
      });
      if (dupe) return;
      drawn.push(p);
      var dot = document.createElementNS(SVG_NS, "circle");
      dot.setAttribute("cx", p.x.toFixed(2));
      dot.setAttribute("cy", p.y.toFixed(2));
      dot.setAttribute("r", "1.6");
      dot.setAttribute("class", "sm-aus-route__stop");
      var title = document.createElementNS(SVG_NS, "title");
      title.textContent = p.label || "";
      dot.appendChild(title);
      layer.appendChild(dot);
    });
    return true;
  }

  /* Towns between Start and Return, without repeats. */
  function townNames(route) {
    var seen = {};
    var first = route.stops[0] && route.stops[0].label;
    return route.stops
      .map(function (stop) { return String(stop.label || "").trim(); })
      .filter(function (label) {
        if (!label || label === first || seen[label]) return false;
        seen[label] = true;
        return true;
      });
  }

  /* "Conder ACT 2906" → "Conder" for a tidy chip. */
  function shortTown(label) {
    return label.replace(/\s+(QLD|NSW|ACT|VIC|TAS|SA|WA|NT)\b.*$/i, "").trim() || label;
  }

  function fillChips(list, route) {
    var towns = townNames(route);
    if (!towns.length) return;
    var MAX = 6;
    list.textContent = "";
    towns.slice(0, MAX).forEach(function (town) {
      var li = document.createElement("li");
      li.className = "sm-van-card__chip";
      li.textContent = shortTown(town);
      list.appendChild(li);
    });
    if (towns.length > MAX) {
      var more = document.createElement("li");
      more.className = "sm-van-card__chip sm-van-card__chip--more";
      more.textContent = "+" + (towns.length - MAX) + " more";
      list.appendChild(more);
    }
    list.setAttribute("aria-label", "Towns on the " + route.name + " run");
  }

  function fillNextRun(box, route) {
    var name = box.querySelector("[data-run-name]");
    var dates = box.querySelector("[data-run-dates]");
    var orderBy = box.querySelector("[data-run-order-by]");
    if (name) name.textContent = route.name || "Van run";
    if (dates) dates.textContent = route.windowLabel || "";
    if (orderBy) orderBy.textContent = route.orderByLabel || "";
    box.querySelectorAll("[data-run-order-by-wrap]").forEach(function (el) {
      el.hidden = !route.orderByLabel;
    });
    box.querySelectorAll("[data-run-dates-wrap]").forEach(function (el) {
      el.hidden = !route.windowLabel;
    });
    box.hidden = false;
  }

  function pointCta(link, route) {
    try {
      var url = new URL(link.getAttribute("href"), window.location.origin);
      url.searchParams.set("route", route.id);
      link.setAttribute("href", url.pathname + url.search + url.hash);
    } catch (err) {
      /* keep the plain delivery page link */
    }
  }

  function mount(section) {
    var appUrl = (section.dataset.appUrl || "").replace(/\/$/, "");
    var shopId = section.dataset.shopId;
    if (!appUrl || !shopId || !window.fetch) return;

    fetch(appUrl + "/api/v1/delivery-routes?shopId=" + encodeURIComponent(shopId), {
      cache: "no-store",
    })
      .then(function (response) {
        if (!response.ok) throw new Error("HTTP " + response.status);
        return response.json();
      })
      .then(function (json) {
        var picked = pickRoute(Array.isArray(json.routes) ? json.routes : []);
        if (!picked) return;
        var route = picked.route;

        var layer = section.querySelector("[data-home-route-layer]");
        if (layer && drawRoute(layer, route)) {
          var svg = layer.ownerSVGElement;
          if (svg) {
            svg.setAttribute("role", "img");
            svg.setAttribute(
              "aria-label",
              "Map of Australia showing the " + route.name + " van run from Carrara"
            );
          }
          section.classList.add("has-route");
        }

        var chips = section.querySelector("[data-run-chips]");
        if (chips) fillChips(chips, route);

        var nextRun = section.querySelector("[data-next-run]");
        if (nextRun && picked.upcoming) fillNextRun(nextRun, route);

        section.querySelectorAll("[data-van-cta]").forEach(function (link) {
          pointCta(link, route);
        });
      })
      .catch(function (err) {
        console.warn("sm-home: delivery routes did not load", err);
      });
  }

  function start() {
    document.querySelectorAll("[data-home-delivery]").forEach(mount);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
